import * as React from 'react';
import { momentCost, momentLabel, type Moment } from '@chess/api-review';

// The reader's blunders and misses in a stage, drawn from props only, as the
// table alone: the title and spacing are the shared stage section frame's
// (`review.stageSection`, from review-stage-section). One row
// per move with what it cost and the engine's move. The whole row is the
// target: a click, or Enter or Space when it has focus, sets the position up
// as a puzzle. The move inside it is a link of its own that only goes to the
// move. `index.tsx` works out the rows; stories pass them by hand.

export interface MomentRow extends Moment {
  /** The engine's move in SAN, when it could be worked out. */
  bestSan?: string;
  /** The reader's record on this move's puzzle. */
  status?: 'solved' | 'failed' | 'solving';
}

export interface MomentsTableProps {
  rows: MomentRow[];
  /** The board's position: the row of that move, when it is a mistake, is marked as where the reader is (2026-10-13). */
  currentPly?: number;
  onGo: (ply: number) => void;
  /** Absent when no puzzles plugin is loaded: the rows are then not targets. */
  onPuzzle?: (row: MomentRow) => void;
}

const KIND: Record<Moment['kind'], string> = { blunder: 'Blunder', miss: 'Miss' };
const ACTION: Record<NonNullable<MomentRow['status']> | 'none', string> = { none: 'Puzzle', solving: 'Solving', solved: 'Solved', failed: 'Retry' };

/** Renders nothing when there are no rows: an empty section is not shown. */
export const MomentsTable: React.FC<MomentsTableProps> = ({ rows, currentPly, onGo, onPuzzle }) => {
  if (rows.length === 0) return null;
  return (
    <table className="moments-table">
        <thead>
          <tr>
            <th scope="col">Move</th>
            <th scope="col">What</th>
            <th scope="col" className="moments-cost">Cost</th>
            <th scope="col">Best</th>
            {onPuzzle && <th scope="col" aria-label="Puzzle" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const start = onPuzzle ? () => onPuzzle(r) : undefined;
            return (
              <tr
                key={r.ply}
                className={`moments-row${start ? ' moments-row-target' : ''}${r.status === 'solving' ? ' moments-row-active' : ''}${r.ply === currentPly ? ' moments-row-current' : ''}`}
                aria-current={r.ply === currentPly ? 'true' : undefined}
                role={start ? 'button' : undefined}
                tabIndex={start ? 0 : undefined}
                title={start ? 'Set up this position and find the better move' : undefined}
                onClick={start}
                onKeyDown={
                  start
                    ? (e) => {
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          start();
                        }
                      }
                    : undefined
                }>
                <td className="moments-move">
                  <button
                    type="button"
                    className="moments-go"
                    title="Go to this move"
                    onClick={(e) => {
                      e.stopPropagation();
                      onGo(r.ply);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}>
                    {momentLabel(r)}
                  </button>
                </td>
                <td className={`moments-kind moments-kind-${r.kind}`}>{KIND[r.kind]}</td>
                <td className="moments-cost">{momentCost(r)}</td>
                <td className="moments-best">{r.bestSan ?? r.best}</td>
                {start && (
                  <td className="moments-action">
                    <span className={`moments-status moments-status-${r.status ?? 'none'}`}>{ACTION[r.status ?? 'none']}</span>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
    </table>
  );
};
